import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ShieldCheck, Receipt } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const Money = ({ v }) => <>${Number(v || 0).toFixed(2)}</>;

export default function PayInvoice() {
  const { orderId } = useParams();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/marketplace/invoice/${orderId}`).then((r) => setInv(r.data)).catch((e) => setErr(errMsg(e)));
  }, [orderId]);

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/marketplace/invoice/${orderId}/pay`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  if (err) return <div className="max-w-lg mx-auto px-4 py-24 text-center"><p className="text-zinc-400" data-testid="invoice-error">{err}</p></div>;
  if (!inv) return <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-rose-500" /></div>;
  const paid = inv.payment_status === "paid";

  return (
    <div className="max-w-lg mx-auto px-4 py-12" data-testid="pay-invoice-page">
      <div className="bg-[#111] border border-white/10 rounded-2xl overflow-hidden">
        <div className="bg-rose-600 px-6 py-4 flex items-center gap-2">
          <Receipt size={18} className="text-white" />
          <p className="font-display font-extrabold text-lg">Invoice {inv.order_number}</p>
        </div>
        <div className="p-6">
          <p className="text-sm text-zinc-400">From <b className="text-white">{inv.seller_name}</b> · {new Date(inv.created_at).toLocaleDateString()}</p>
          <p className="text-sm text-zinc-400">Billed to {inv.buyer_name || inv.buyer_email}</p>
          <div className="mt-5 divide-y divide-white/5">
            {(inv.items || []).map((it, i) => (
              <div key={i} className="flex items-center justify-between gap-3 py-3" data-testid={`invoice-item-${i}`}>
                <div className="min-w-0">
                  <p className="font-bold text-sm truncate">{it.product_name}{it.variation_name ? ` — ${it.variation_name}` : ""}</p>
                  <p className="text-xs text-zinc-500">Qty {it.qty} · <Money v={it.unit_price} /> each</p>
                </div>
                <p className="font-bold text-sm shrink-0"><Money v={it.line_total} /></p>
              </div>
            ))}
          </div>
          <div className="mt-4 space-y-1 text-sm text-zinc-300">
            <div className="flex justify-between"><span>Subtotal</span><Money v={inv.subtotal} /></div>
            {Number(inv.shipping) > 0 && <div className="flex justify-between"><span>Shipping</span><Money v={inv.shipping} /></div>}
            {Number(inv.buyer_fee) > 0 && <div className="flex justify-between"><span>Service fee</span><Money v={inv.buyer_fee} /></div>}
            {Number(inv.tax) > 0 && <div className="flex justify-between"><span>Tax</span><Money v={inv.tax} /></div>}
            <div className="flex justify-between font-extrabold text-white text-base pt-2 border-t border-white/10">
              <span>{paid ? "Amount paid" : "Amount due"}</span><span data-testid="invoice-total"><Money v={inv.total} /></span>
            </div>
          </div>
          {inv.order_notes && <p className="text-xs text-zinc-500 mt-3"><b>Notes:</b> {inv.order_notes}</p>}

          {paid ? (
            <div className="mt-6 flex items-center gap-2 text-emerald-300 font-bold" data-testid="invoice-paid">
              <CheckCircle2 size={18} /> Paid — thank you!
            </div>
          ) : inv.payment_status === "cancelled" ? (
            <p className="mt-6 text-zinc-400 text-sm">This invoice has been cancelled.</p>
          ) : inv.invoice_mode === "payment_link" ? (
            <>
              <button onClick={pay} disabled={busy} data-testid="invoice-pay-btn"
                className="mt-6 w-full bg-rose-500 hover:bg-rose-600 disabled:opacity-40 font-bold py-3.5 rounded-full flex items-center justify-center gap-2">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />} Pay <Money v={inv.total} /> securely
              </button>
              <p className="text-[11px] text-zinc-500 text-center mt-2">Secure card payment powered by Stripe.</p>
            </>
          ) : (
            <p className="mt-6 text-sm text-zinc-400 bg-white/5 rounded-xl p-4" data-testid="invoice-manual-note">
              This invoice is payable directly to the seller (cash, check or other arrangement). Contact them to complete payment.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
